import type {
  MemberCommunicationAttachmentDto,
  MemberCommunicationAttachmentType,
  MemberCommunicationCategory,
  MemberCommunicationDto,
  ViewerMemberCommunicationDto
} from "@madamuh/domain";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  createAdminCommunicationThumbnailAccessUrl,
  createFamilyCommunicationAttachmentAccessUrl,
  createFamilyCommunicationThumbnailAccessUrl,
  createTeacherCommunicationThumbnailAccessUrl,
  markFamilyMemberCommunicationRead
} from "../api/memberCommunication";
import {
  formatCommunicationPublishedAtJa,
  formatMediaDuration
} from "../lib/memberCommunicationDisplay";
import { describeStatus } from "../lib/uiLabels";
import {
  ChevronRightIcon,
  DocumentIcon,
  ImageIcon,
  MessageIcon,
  PlayCircleIcon
} from "./icons/AppIcons";

type ThumbnailSurface = "family" | "admin" | "teacher";

type MediaViewerState = {
  attachment: MemberCommunicationAttachmentDto;
  sourceUrl: string;
  posterUrl: string | null;
};

export const COMMUNICATION_CATEGORY_LABELS: Record<MemberCommunicationCategory, string> = {
  general: "通常の連絡",
  lessonSupport: "レッスン補足",
  replacement: "代替対応",
  eventSupport: "イベント補足"
};

export const COMMUNICATION_ATTACHMENT_LABELS: Record<MemberCommunicationAttachmentType, string> = {
  image: "画像",
  video: "動画",
  pdf: "PDF"
};

export function CommunicationAttachmentIcon({
  attachmentType,
  size = 20
}: {
  attachmentType?: MemberCommunicationAttachmentType | null;
  size?: number;
}) {
  if (attachmentType === "image") {
    return <ImageIcon size={size} />;
  }
  if (attachmentType === "video") {
    return <PlayCircleIcon size={size} />;
  }
  if (attachmentType === "pdf") {
    return <DocumentIcon size={size} />;
  }
  return <MessageIcon size={size} />;
}

export function CommunicationUnreadBadge({ isUnread }: { isUnread?: boolean }) {
  if (!isUnread) {
    return null;
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 22,
        padding: "2px 8px",
        borderRadius: 999,
        background: "#fff8db",
        color: "#7a4b00",
        fontSize: 12,
        fontWeight: 700
      }}
    >
      未読
    </span>
  );
}

export function getCommunicationTitle(communication: Pick<MemberCommunicationDto, "title" | "body" | "targetLabel">): string {
  if (communication.title?.trim()) {
    return communication.title.trim();
  }
  const firstBodyLine = communication.body
    ?.split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean);
  if (firstBodyLine) {
    return firstBodyLine;
  }
  if (communication.targetLabel.trim()) {
    return `${communication.targetLabel}の連絡`;
  }
  return "連絡事項";
}

export function getCommunicationExcerpt(body: string | null, maxLength = 110): string | null {
  if (!body?.trim()) {
    return null;
  }
  const normalized = body.trim().replace(/\n{3,}/g, "\n\n");
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}…` : normalized;
}

function attachmentSummary(attachments: readonly MemberCommunicationAttachmentDto[]): string | null {
  if (attachments.length === 0) {
    return null;
  }
  const first = attachments[0];
  if (!first) {
    return null;
  }
  const label = COMMUNICATION_ATTACHMENT_LABELS[first.attachmentType];
  return attachments.length > 1 ? `${label}ほか ${attachments.length}件` : label;
}

function firstAttachmentType(attachments: readonly MemberCommunicationAttachmentDto[]): MemberCommunicationAttachmentType | null {
  return attachments[0]?.attachmentType ?? null;
}

function firstRenderableAttachment(attachments: readonly MemberCommunicationAttachmentDto[]): MemberCommunicationAttachmentDto | null {
  return attachments.find((attachment) => attachment.uploadStatus === "uploaded") ?? null;
}

function firstRenderableVideoAttachment(attachments: readonly MemberCommunicationAttachmentDto[]): MemberCommunicationAttachmentDto | null {
  return attachments.find((attachment) => attachment.uploadStatus === "uploaded" && attachment.attachmentType === "video") ?? null;
}

function isThumbnailCandidate(attachment: MemberCommunicationAttachmentDto | null): attachment is MemberCommunicationAttachmentDto {
  return Boolean(
    attachment &&
      attachment.uploadStatus === "uploaded" &&
      attachment.thumbnailAvailable &&
      (attachment.attachmentType === "image" || attachment.attachmentType === "video")
  );
}

async function createThumbnailAccessUrl(surface: ThumbnailSurface, tenantSlug: string, communicationId: string, attachmentId: string) {
  if (surface === "admin") {
    return await createAdminCommunicationThumbnailAccessUrl(tenantSlug, communicationId, attachmentId);
  }
  if (surface === "teacher") {
    return await createTeacherCommunicationThumbnailAccessUrl(tenantSlug, communicationId, attachmentId);
  }
  return await createFamilyCommunicationThumbnailAccessUrl(tenantSlug, communicationId, attachmentId);
}

function useLazyCommunicationThumbnail({
  tenantSlug,
  communicationId,
  attachment,
  surface
}: {
  tenantSlug?: string | undefined;
  communicationId: string;
  attachment: MemberCommunicationAttachmentDto | null;
  surface?: ThumbnailSurface | undefined;
}) {
  const elementRef = useRef<HTMLButtonElement | HTMLSpanElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "fallback">("idle");
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return undefined;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    if (rect.bottom >= -160 && rect.right >= 0 && rect.top <= viewportHeight + 160 && rect.left <= viewportWidth) {
      setIsVisible(true);
    }

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
        }
      },
      { rootMargin: "160px" }
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const loadThumbnail = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }
    if (!tenantSlug || !surface || !isThumbnailCandidate(attachment)) {
      setLoadState("fallback");
      return;
    }

    setLoadState("loading");
    try {
      const response = await createThumbnailAccessUrl(surface, tenantSlug, communicationId, attachment.attachmentId);
      if (!isMountedRef.current) {
        return;
      }
      setThumbnailUrl(response.accessUrl);
      setLoadState("ready");
    } catch {
      if (!isMountedRef.current) {
        return;
      }
      setThumbnailUrl(null);
      setLoadState("fallback");
    }
  }, [attachment, communicationId, surface, tenantSlug]);

  useEffect(() => {
    retryCountRef.current = 0;
    setThumbnailUrl(null);
    setLoadState("idle");
  }, [attachment?.attachmentId, communicationId, surface, tenantSlug]);

  useEffect(() => {
    if (isVisible && loadState === "idle") {
      void loadThumbnail();
    }
  }, [isVisible, loadState, loadThumbnail]);

  const retryOnceAfterImageError = useCallback(() => {
    if (retryCountRef.current > 0) {
      setThumbnailUrl(null);
      setLoadState("fallback");
      return;
    }

    retryCountRef.current += 1;
    void loadThumbnail();
  }, [loadThumbnail]);

  return {
    elementRef,
    thumbnailUrl,
    loadState,
    retryOnceAfterImageError
  };
}

function CommunicationMediaViewer({
  media,
  onClose
}: {
  media: MediaViewerState;
  onClose: () => void;
}) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${COMMUNICATION_ATTACHMENT_LABELS[media.attachment.attachmentType]}を表示`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "grid",
        placeItems: "center",
        padding: 16,
        background: "rgba(20, 20, 20, 0.72)"
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "relative",
          width: "min(960px, 100%)",
          maxHeight: "90vh",
          display: "grid",
          gap: 10
        }}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          style={{
            justifySelf: "end",
            border: "none",
            borderRadius: 999,
            padding: "8px 12px",
            background: "white",
            fontWeight: 700
          }}
        >
          閉じる
        </button>
        {media.attachment.attachmentType === "video" ? (
          <video
            controls
            playsInline
            poster={media.posterUrl ?? undefined}
            src={media.sourceUrl}
            style={{
              width: "100%",
              maxHeight: "82vh",
              borderRadius: 16,
              background: "black"
            }}
          />
        ) : (
          <img
            alt={media.attachment.originalFileName ?? "添付画像"}
            src={media.sourceUrl}
            style={{
              width: "100%",
              maxHeight: "82vh",
              objectFit: "contain",
              borderRadius: 16,
              background: "white"
            }}
          />
        )}
      </div>
    </div>
  );
}

export function CommunicationThumbnailButton({
  communicationId,
  attachment,
  tenantSlug,
  surface,
  compact = false,
  onOpen
}: {
  communicationId: string;
  attachment: MemberCommunicationAttachmentDto | null;
  tenantSlug?: string | undefined;
  surface?: ThumbnailSurface | undefined;
  compact?: boolean;
  onOpen?: ((attachment: MemberCommunicationAttachmentDto, thumbnailUrl: string | null) => void) | undefined;
}) {
  const { elementRef, thumbnailUrl, loadState, retryOnceAfterImageError } = useLazyCommunicationThumbnail({
    tenantSlug,
    communicationId,
    attachment,
    surface
  });
  const size = compact ? 52 : 68;
  const canOpen = Boolean(attachment && onOpen);
  const sharedStyle = {
    width: size,
    height: Math.round((size * 9) / 16),
    minWidth: size,
    borderRadius: 12,
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    background: "#f4f4f4",
    color: "#555",
    border: "1px solid #e8e8e8",
    position: "relative" as const
  };
  const contents = (
    <>
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          onError={retryOnceAfterImageError}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <CommunicationAttachmentIcon attachmentType={attachment?.attachmentType ?? null} size={compact ? 20 : 24} />
      )}
      {attachment?.attachmentType === "video" ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            background: thumbnailUrl ? "rgba(0, 0, 0, 0.18)" : "transparent",
            color: "white",
            textShadow: "0 1px 4px rgba(0,0,0,0.45)"
          }}
        >
          <PlayCircleIcon size={compact ? 24 : 30} />
        </span>
      ) : null}
      {loadState === "loading" ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 5,
            bottom: 5,
            width: 8,
            height: 8,
            borderRadius: 999,
            background: "#bbb"
          }}
        />
      ) : null}
    </>
  );

  if (!attachment) {
    return (
      <span ref={(node) => { elementRef.current = node; }} aria-hidden="true" className="family-list-card__icon" style={{ ...sharedStyle, flexBasis: size }}>
        <CommunicationAttachmentIcon attachmentType={null} size={compact ? 20 : 24} />
      </span>
    );
  }

  if (!canOpen) {
    return (
      <span
        ref={(node) => { elementRef.current = node; }}
        className="family-list-card__icon"
        aria-label={`${COMMUNICATION_ATTACHMENT_LABELS[attachment.attachmentType]}添付`}
        style={{ ...sharedStyle, flexBasis: size }}
      >
        {contents}
      </span>
    );
  }

  return (
    <button
      ref={(node) => { elementRef.current = node; }}
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen?.(attachment, thumbnailUrl);
      }}
      aria-label={`${COMMUNICATION_ATTACHMENT_LABELS[attachment.attachmentType]}を開く`}
      style={{
        ...sharedStyle,
        flexBasis: size,
        padding: 0,
        cursor: "pointer"
      }}
    >
      {contents}
    </button>
  );
}

export function MemberCommunicationVideoThumbnail({
  communicationId,
  attachment,
  tenantSlug,
  surface,
  compact = false,
  onOpen
}: {
  communicationId: string;
  attachment: MemberCommunicationAttachmentDto;
  tenantSlug?: string | undefined;
  surface?: ThumbnailSurface | undefined;
  compact?: boolean;
  onOpen?: ((attachment: MemberCommunicationAttachmentDto, thumbnailUrl: string | null) => void) | undefined;
}) {
  const { elementRef, thumbnailUrl, loadState, retryOnceAfterImageError } = useLazyCommunicationThumbnail({
    tenantSlug,
    communicationId,
    attachment,
    surface
  });
  const canOpen = Boolean(onOpen);
  const durationLabel = formatMediaDuration(attachment.durationSeconds);
  const contents = (
    <>
      {thumbnailUrl ? (
        <img
          className="communication-video-preview__image"
          src={thumbnailUrl}
          alt=""
          onError={retryOnceAfterImageError}
        />
      ) : (
        <span className="communication-video-preview__fallback">
          {loadState === "loading" ? "プレビュー準備中" : ""}
        </span>
      )}
      <span
        className="communication-video-preview__play"
        aria-hidden="true"
      >
        <span className="communication-video-preview__play-triangle" />
      </span>
      {durationLabel ? (
        <span className="communication-video-preview__duration">
          {durationLabel}
        </span>
      ) : null}
    </>
  );

  if (!canOpen) {
    return (
      <span
        ref={(node) => { elementRef.current = node; }}
        className={`communication-video-preview${compact ? " communication-video-preview--compact" : ""}`}
        aria-label="動画添付"
      >
        {contents}
      </span>
    );
  }

  return (
    <button
      ref={(node) => { elementRef.current = node; }}
      type="button"
      className={`communication-video-preview communication-video-preview--button${compact ? " communication-video-preview--compact" : ""}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen?.(attachment, thumbnailUrl);
      }}
      aria-label={`${attachment.originalFileName ?? "動画"}を再生`}
    >
      {contents}
    </button>
  );
}

export function MemberCommunicationCard({
  communication,
  to,
  timeZone = "Asia/Tokyo",
  compact = false,
  tenantSlug,
  thumbnailSurface,
  enableFamilyAttachmentOpen = false
}: {
  communication: MemberCommunicationDto | ViewerMemberCommunicationDto;
  to?: string;
  timeZone?: string;
  compact?: boolean;
  tenantSlug?: string | undefined;
  thumbnailSurface?: ThumbnailSurface | undefined;
  enableFamilyAttachmentOpen?: boolean;
}) {
  const viewerState = communication as Partial<ViewerMemberCommunicationDto>;
  const title = getCommunicationTitle(communication);
  const excerpt = getCommunicationExcerpt(communication.body, compact ? 72 : 120);
  const attachmentsLabel = attachmentSummary(communication.attachments);
  const publishedLabel = communication.publishedAt ?? communication.publishFrom ?? communication.updatedAt;
  const primaryAttachment = firstRenderableAttachment(communication.attachments);
  const primaryVideoAttachment = firstRenderableVideoAttachment(communication.attachments);
  const [viewerMedia, setViewerMedia] = useState<MediaViewerState | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const canOpenFamilyAttachment = enableFamilyAttachmentOpen && Boolean(tenantSlug);
  const titleBlock = (
    <span style={{ display: "grid", gap: 4, minWidth: 0 }}>
      <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <strong className="family-list-card__title family-break-anywhere">{title}</strong>
        <CommunicationUnreadBadge isUnread={Boolean(viewerState.isUnread)} />
      </span>
      <span className="family-list-card__support family-break-anywhere" style={{ whiteSpace: "pre-line" }}>
        {excerpt ?? "本文はありません。"}
      </span>
    </span>
  );

  const openAttachment = useCallback(
    async (attachment: MemberCommunicationAttachmentDto, thumbnailUrl: string | null) => {
      if (!tenantSlug || !canOpenFamilyAttachment) {
        return;
      }

      setOpenError(null);
      try {
        await markFamilyMemberCommunicationRead(tenantSlug, communication.communicationId);
        const response = await createFamilyCommunicationAttachmentAccessUrl(
          tenantSlug,
          communication.communicationId,
          attachment.attachmentId
        );

        if (attachment.attachmentType === "pdf") {
          window.open(response.accessUrl, "_blank", "noopener,noreferrer");
          return;
        }

        setViewerMedia({
          attachment,
          sourceUrl: response.accessUrl,
          posterUrl: thumbnailUrl
        });
      } catch {
        setOpenError("添付を開けませんでした。時間をおいてもう一度お試しください。");
      }
    },
    [canOpenFamilyAttachment, communication.communicationId, tenantSlug]
  );

  const metaBlock = (
    <>
      <span className="family-list-card__meta">
        <span>{communication.targetLabel}</span>
        <span>{formatCommunicationPublishedAtJa(publishedLabel, timeZone)}</span>
      </span>
      <span className="family-list-card__meta family-list-card__meta--tight">
        <span className="family-badge-chip" style={{ background: "#eef6ff" }}>
          {COMMUNICATION_CATEGORY_LABELS[communication.category] ?? describeStatus(communication.category)}
        </span>
        {attachmentsLabel ? (
          <span className="family-badge-chip" style={{ background: "#f6f6f6" }}>
            添付: {attachmentsLabel}
          </span>
        ) : null}
      </span>
      {openError ? <span className="family-list-card__support">{openError}</span> : null}
    </>
  );
  const detailContent = (
    <>
      <span className="family-list-card__title-row">
        {titleBlock}
        {to ? (
          <span className="family-list-card__chevron" aria-hidden="true">
            <ChevronRightIcon size={18} />
          </span>
        ) : null}
      </span>
      {metaBlock}
    </>
  );
  const detailLabel = `${title}の連絡内容を開く`;

  const standardContent = (
    <>
      <CommunicationThumbnailButton
        communicationId={communication.communicationId}
        attachment={primaryAttachment}
        tenantSlug={tenantSlug}
        surface={thumbnailSurface}
        compact={compact}
        onOpen={canOpenFamilyAttachment ? openAttachment : undefined}
      />
      <span className="family-list-card__main">
        {to ? (
          <Link className="member-communication-card__detail-link" to={to} aria-label={detailLabel}>
            {detailContent}
          </Link>
        ) : (
          detailContent
        )}
      </span>
    </>
  );
  const featuredVideoContent = primaryVideoAttachment ? (
    <span style={{ display: "grid", gap: 12, width: "100%" }}>
      <MemberCommunicationVideoThumbnail
        communicationId={communication.communicationId}
        attachment={primaryVideoAttachment}
        tenantSlug={tenantSlug}
        surface={thumbnailSurface}
        compact={compact}
        onOpen={canOpenFamilyAttachment ? openAttachment : undefined}
      />
      <span className="family-list-card__main">
        {to ? (
          <Link className="member-communication-card__detail-link" to={to} aria-label={detailLabel}>
            {detailContent}
          </Link>
        ) : (
          detailContent
        )}
      </span>
    </span>
  ) : null;
  const content = featuredVideoContent ?? standardContent;

  if (to) {
    return (
      <>
        <article className={`family-list-card member-communication-card${primaryVideoAttachment ? " member-communication-card--video" : ""}`} style={{ gap: 12, minHeight: 44 }}>
          {content}
        </article>
        {viewerMedia ? <CommunicationMediaViewer media={viewerMedia} onClose={() => setViewerMedia(null)} /> : null}
      </>
    );
  }

  return (
    <>
      <article className={`family-list-card member-communication-card${primaryVideoAttachment ? " member-communication-card--video" : ""}`} style={{ gap: 12, minHeight: 44 }}>
        {content}
      </article>
      {viewerMedia ? <CommunicationMediaViewer media={viewerMedia} onClose={() => setViewerMedia(null)} /> : null}
    </>
  );
}
